import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';

const DELAY_STEPS = Array.from({ length: 12 }, (_, index) => (index + 1) * 5);

const setPrerenderReady = (value) => {
  if (typeof window !== 'undefined') {
    window.prerenderReady = value;
  }
};

const normalizePath = (path) => {
  if (!path) {
    return '/';
  }
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
};

const getInitialPath = () => {
  if (typeof window === 'undefined' || !window.location) {
    return '/';
  }
  return normalizePath(window.location.pathname);
};

const parseRoute = (path) => {
  if (path === '/') {
    return { type: 'home' };
  }

  const match = path.match(/^\/delay\/(\d{1,3})$/);
  if (match) {
    const seconds = Number(match[1]);
    if (DELAY_STEPS.includes(seconds)) {
      return { type: 'delay', seconds };
    }
  }

  return { type: 'not-found' };
};

function DelayLink({ to, onNavigate, isActive, children, className }) {
  const handleClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();
    onNavigate(to);
  };

  return (
    <a
      className={`${className ?? ''}${isActive ? ' active' : ''}`.trim()}
      href={to}
      onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
    >
      {children}
    </a>
  );
}

function HomePage({ delays, onNavigate, currentPath }) {
  useEffect(() => {
    setPrerenderReady(true);
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <h1>Throttled Delay Playground</h1>
        <p>
          Use these routes to simulate pages that deliberately wait before rendering content.
          Each path resolves fully on the client after the specified delay.
        </p>
      </header>

      <section className="delay-grid" aria-label="List of throttled routes">
        {delays.map((seconds) => {
          const route = `/delay/${seconds}`;
          return (
            <article key={seconds} className="delay-card">
              <h2>{seconds} second delay</h2>
              <p>Navigate to deliberately wait {seconds} seconds before content appears.</p>
              <DelayLink
                to={route}
                onNavigate={onNavigate}
                isActive={currentPath === route}
                className="route-link"
              >
                Open {route}
              </DelayLink>
            </article>
          );
        })}
      </section>
    </div>
  );
}

function DelayPage({ seconds, onNavigate }) {
  const [isLoading, setIsLoading] = useState(true);
  const [remainingMs, setRemainingMs] = useState(seconds * 1000);

  useEffect(() => {
    setIsLoading(true);
    setRemainingMs(seconds * 1000);

    setPrerenderReady(false);

    const startedAt = Date.now();

    const countdownId = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(seconds * 1000 - elapsed, 0);
      setRemainingMs(remaining);
      if (remaining === 0) {
        window.clearInterval(countdownId);
      }
    }, 200);

    const timerId = window.setTimeout(() => {
      setRemainingMs(0);
      setIsLoading(false);
      setPrerenderReady(true);
      window.clearInterval(countdownId);
    }, seconds * 1000);

    return () => {
      window.clearInterval(countdownId);
      window.clearTimeout(timerId);
      setPrerenderReady(false);
    };
  }, [seconds]);

  const remainingSeconds = Math.ceil(remainingMs / 1000);

  return (
    <div className="page">
      <header className="page-header">
        <h1>{seconds} second delay</h1>
        <p>
          This route mimics a slow page by waiting {seconds} seconds before revealing its content.
        </p>
      </header>

      {isLoading ? (
        <section className="loading-state" aria-live="polite">
          <div className="spinner" aria-hidden="true" />
          <p className="countdown">Releasing content in ~{remainingSeconds} seconds</p>
        </section>
      ) : (
        <section className="loaded-state">
          <h2>All set!</h2>
          <p>
            The throttled content is now available after waiting {seconds} seconds. Use this page to
            verify pre-rendering or bot behaviour with delayed responses.
          </p>
          <div className="loaded-actions">
            <DelayLink to="/" onNavigate={onNavigate} className="route-link">
              Back to delay index
            </DelayLink>
          </div>
        </section>
      )}
    </div>
  );
}

function NotFoundPage({ onNavigate }) {
  useEffect(() => {
    setPrerenderReady(true);
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <h1>Not Found</h1>
        <p>The requested delay route does not exist.</p>
      </header>
      <div className="loaded-actions">
        <DelayLink to="/" onNavigate={onNavigate} className="route-link">
          View available delays
        </DelayLink>
      </div>
    </div>
  );
}

function App() {
  const [currentPath, setCurrentPath] = useState(getInitialPath);

  const navigate = useCallback(
    (targetPath) => {
      const normalizedTarget = normalizePath(targetPath);
      if (normalizedTarget === currentPath) {
        return;
      }
      if (typeof window !== 'undefined' && window.history) {
        window.history.pushState(null, '', normalizedTarget);
      }
      setPrerenderReady(false);
      setCurrentPath(normalizedTarget);
    },
    [currentPath]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePopState = () => {
      setCurrentPath(normalizePath(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const route = useMemo(() => parseRoute(currentPath), [currentPath]);

  return (
    <div className="App">
      <div className="app-shell">
        <nav className="app-nav">
          <DelayLink to="/" onNavigate={navigate} isActive={currentPath === '/'} className="brand">
            Prerender Bot Page
          </DelayLink>
        </nav>
        <main>
          {route.type === 'home' && (
            <HomePage delays={DELAY_STEPS} onNavigate={navigate} currentPath={currentPath} />
          )}
          {route.type === 'delay' && <DelayPage seconds={route.seconds} onNavigate={navigate} />}
          {route.type === 'not-found' && <NotFoundPage onNavigate={navigate} />}
        </main>
      </div>
    </div>
  );
}

export default App;
